// Cell value formatters. Pure functions — used by both the cell
// components and unit tests. Locale defaults to the operator's browser
// locale (which is what the rest of the portal does too).

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—"
  return new Intl.NumberFormat().format(value)
}

export function formatMoneyUsd(value: number | null | undefined, currency: string = "USD"): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—"
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value)
  } catch {
    return `$${value.toFixed(2)}`
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(d)
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(d)
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "—"
  if (ms < 1000) return `${Math.round(ms)}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = seconds / 60
  if (minutes < 60) return `${minutes.toFixed(0)}m`
  const hours = minutes / 60
  if (hours < 24) return `${hours.toFixed(1)}h`
  const days = hours / 24
  return `${days.toFixed(1)}d`
}

// Tone classes shared across blocks/cells. Map the wire-level tone enum
// onto Valet status tokens so tone is visually consistent with other
// surfaces (TicketStatusBadge, escalation chips, SLA bars).
export type ToneCss = { text: string; bg: string; border: string; dot: string }

export const TONE_CSS: Record<string, ToneCss> = {
  neutral: {
    text:   "text-app-text",
    bg:     "bg-app-panel2",
    border: "border-border",
    dot:    "bg-app-muted",
  },
  success: {
    text:   "text-status-success",
    bg:     "bg-status-success/10",
    border: "border-status-success/30",
    dot:    "bg-status-success",
  },
  warning: {
    text:   "text-status-warning",
    bg:     "bg-status-warning/10",
    border: "border-status-warning/30",
    dot:    "bg-status-warning",
  },
  danger: {
    text:   "text-status-error",
    bg:     "bg-status-error/10",
    border: "border-status-error/30",
    dot:    "bg-status-error",
  },
  info: {
    text:   "text-app-text",
    bg:     "bg-brand-redSoft",
    border: "border-brand-red/30",
    dot:    "bg-brand-red",
  },
}

export function toneCss(tone: string | undefined): ToneCss {
  return TONE_CSS[tone ?? "neutral"] ?? TONE_CSS.neutral
}
